import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { Box, Divider } from '@mui/material';
import { useLocation } from 'react-router-dom';

import CanvasComments from '@/designs-dev/comments/CanvasComments';
import {
  setListOpen,
  hasUnread,
  openThreads,
  useComments,
} from '@/designs-dev/comments/store';
import { Pill, Segment } from '@/designs-dev/inspect/Pill';

/**
 * Set inside a canvas. A flow row reads it to stop scrolling on its own and run
 * its full width, and a mini reads it to watch the canvas rather than the window
 * for when it is near enough to render.
 */
export const CanvasContext = createContext<{ root: HTMLElement | null } | null>(
  null,
);

const MIN = 0.02;
const MAX = 4;
// Where ⌘+ and ⌘− land, the way Figma's do: round numbers, closer together
// near 100% where the difference is something you read.
const STEPS = [0.02, 0.05, 0.1, 0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4];

const clamp = (z: number) => Math.min(MAX, Math.max(MIN, z));

type View = { x: number; y: number; z: number };

// Keys go to whatever is being typed in before they go to the canvas. A mini is
// a live screen with inputs in it, and space in a text field is a space.
// Whether something between the pointer and the canvas can still scroll the
// way the wheel is going.
const scrollsInside = (e: WheelEvent, root: HTMLElement) => {
  const dx = e.shiftKey && e.deltaX === 0 ? e.deltaY : e.deltaX;
  const dy = e.shiftKey && e.deltaX === 0 ? 0 : e.deltaY;
  for (
    let el = e.target instanceof Element ? e.target : null;
    el && el !== root;
    el = el.parentElement
  ) {
    const style = getComputedStyle(el);
    const canY = /(auto|scroll)/.test(style.overflowY);
    const canX = /(auto|scroll)/.test(style.overflowX);
    if (
      canY &&
      ((dy > 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 1) ||
        (dy < 0 && el.scrollTop > 0))
    )
      return true;
    if (
      canX &&
      ((dx > 0 && el.scrollLeft + el.clientWidth < el.scrollWidth - 1) ||
        (dx < 0 && el.scrollLeft > 0))
    )
      return true;
  }
  return false;
};

const typing = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

/**
 * A page you move around like a Figma file.
 *
 * - two fingers, or the wheel, pan; shift and the wheel pans sideways. Over a
 *   screen that scrolls, it scrolls that instead, until it reaches its end
 * - pinch, or ⌘ and the wheel, zooms on the pointer
 * - space and drag, or the middle button, drags the page
 * - ⌘+ and ⌘− step the zoom, shift 0 is 100% and shift 1 fits the page
 * - shift 2 goes back to where the page opened: 100%, from the top left
 *
 * Opens at 100% from the top left, so it looks like the page it replaced until
 * you move it.
 *
 * The transform is written to the layer directly rather than through state. A
 * flows page is dozens of live screens, and rendering them again on every wheel
 * event is the difference between a pinch that follows your fingers and one
 * that does not.
 */
const Canvas = ({
  children,
  tools,
}: {
  children: ReactNode;
  // Controls the page brings to the floating bar, ahead of commenting and zoom:
  // a versioned page's version and its changes.
  tools?: ReactNode;
}) => {
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const layer = useRef<HTMLDivElement>(null);
  const view = useRef<View>({ x: 0, y: 0, z: 1 });
  const settle = useRef<number>(undefined);
  const space = useRef(false);
  const drag = useRef<{ px: number; py: number; x: number; y: number } | null>(
    null,
  );
  const [zoom, setZoom] = useState(1);
  const [hand, setHand] = useState<'ready' | 'dragging' | null>(null);
  const [commenting, setCommenting] = useState(false);
  const comments = useComments();
  const { user } = useKindeAuth();
  const { listOpen } = comments;
  // The layer as state too, for the comment pins portalled into it.
  const [layerEl, setLayerEl] = useState<HTMLDivElement | null>(null);
  const layerRef = useCallback((el: HTMLDivElement | null) => {
    layer.current = el;
    setLayerEl(el);
  }, []);

  const apply = useCallback((next: View) => {
    view.current = next;
    const el = layer.current;
    if (!el) return;
    el.style.transform = `translate(${next.x}px, ${next.y}px) scale(${next.z})`;
    // Held as one picture while it moves and drawn sharp again once it stops,
    // which is what Figma does too. Without the hint every frame of a pinch
    // lays the text out again at the new size.
    el.style.willChange = 'transform';
    window.clearTimeout(settle.current);
    settle.current = window.setTimeout(() => {
      el.style.willChange = '';
    }, 150);
    setZoom(next.z);
  }, []);

  // Zoom to z keeping the point (cx, cy) of the canvas where it is.
  const zoomAt = useCallback(
    (z: number, cx: number, cy: number) => {
      const { x, y, z: from } = view.current;
      const to = clamp(z);
      apply({
        z: to,
        x: cx - ((cx - x) * to) / from,
        y: cy - ((cy - y) * to) / from,
      });
    },
    [apply],
  );

  const zoomCentre = useCallback(
    (z: number) => {
      if (!root) return;
      zoomAt(z, root.clientWidth / 2, root.clientHeight / 2);
    },
    [root, zoomAt],
  );

  const step = useCallback(
    (direction: 1 | -1) => {
      const z = view.current.z;
      const next =
        direction === 1
          ? (STEPS.find((s) => s > z * 1.01) ?? MAX)
          : ([...STEPS].reverse().find((s) => s < z * 0.99) ?? MIN);
      zoomCentre(next);
    },
    [zoomCentre],
  );

  const home = useCallback(() => apply({ x: 0, y: 0, z: 1 }), [apply]);

  // A new page from the menu opens at its top left, as Shift+2 does. The canvas
  // stays mounted between pages, so it would otherwise keep where the last page
  // was panned to, which on a shorter page is often nothing at all.
  const { pathname } = useLocation();
  useEffect(() => {
    home();
  }, [pathname, home]);

  // Puts a point of the layer in the middle of the view, at the current zoom.
  const focus = useCallback(
    (x: number, y: number) => {
      if (!root) return;
      const { z } = view.current;
      apply({
        z,
        x: root.clientWidth / 2 - x * z,
        y: root.clientHeight / 2 - y * z,
      });
    },
    [root, apply],
  );

  const fit = useCallback(() => {
    const el = layer.current;
    if (!root || !el) return;
    // Layout size, which the transform does not touch.
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const z = clamp(
      Math.min(root.clientWidth / w, root.clientHeight / h) * 0.95,
    );
    apply({
      z,
      x: (root.clientWidth - w * z) / 2,
      y: (root.clientHeight - h * z) / 2,
    });
  }, [root, apply]);

  // A real listener rather than onWheel: React's is passive, and a passive
  // listener cannot stop the browser zooming the whole tab on a pinch.
  useEffect(() => {
    if (!root) return;
    const onWheel = (e: WheelEvent) => {
      // A plain scroll over something that scrolls goes to it: a screen is a
      // live prototype, and its chat has to scroll under the pointer. Only
      // once it has reached its end does the page take over and pan.
      if (!e.ctrlKey && !e.metaKey && scrollsInside(e, root)) return;
      e.preventDefault();
      const unit =
        e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? root.clientHeight : 1;
      let dx = e.deltaX * unit;
      let dy = e.deltaY * unit;
      // A pinch arrives as the wheel with ctrl held.
      if (e.ctrlKey || e.metaKey) {
        const r = root.getBoundingClientRect();
        // A mouse wheel's notch is a hundred pixels and a pinch's movement is a
        // few, so one notch is capped at a sensible step rather than a leap.
        const d = Math.max(-25, Math.min(25, dy));
        zoomAt(
          view.current.z * Math.exp(-d * 0.01),
          e.clientX - r.left,
          e.clientY - r.top,
        );
        return;
      }
      // Shift and the wheel goes sideways, which a Mac does already and other
      // systems leave to the page.
      if (e.shiftKey && dx === 0) {
        dx = dy;
        dy = 0;
      }
      const v = view.current;
      apply({ ...v, x: v.x - dx, y: v.y - dy });
    };
    root.addEventListener('wheel', onWheel, { passive: false });
    return () => root.removeEventListener('wheel', onWheel);
  }, [root, apply, zoomAt]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (typing(e.target)) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        step(1);
      } else if (mod && e.key === '-') {
        e.preventDefault();
        step(-1);
      } else if (
        (mod && e.key === '0') ||
        (e.shiftKey && e.code === 'Digit0')
      ) {
        e.preventDefault();
        zoomCentre(1);
      } else if (e.shiftKey && e.code === 'Digit2') {
        e.preventDefault();
        home();
      } else if (e.shiftKey && e.code === 'Digit1') {
        e.preventDefault();
        fit();
      } else if (e.code === 'Space') {
        e.preventDefault();
        if (!e.repeat) {
          space.current = true;
          if (!drag.current) setHand('ready');
        }
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      space.current = false;
      if (!drag.current) setHand(null);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [step, zoomCentre, fit, home]);

  // On capture, so a drag that starts on a mini moves the page instead of
  // pressing whatever is under it.
  const onPointerDownCapture = (e: React.PointerEvent) => {
    if (!(e.button === 1 || (e.button === 0 && space.current))) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const { x, y } = view.current;
    drag.current = { px: e.clientX, py: e.clientY, x, y };
    setHand('dragging');
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    apply({
      ...view.current,
      x: d.x + e.clientX - d.px,
      y: d.y + e.clientY - d.py,
    });
  };

  const onPointerUp = () => {
    if (!drag.current) return;
    drag.current = null;
    setHand(space.current ? 'ready' : null);
  };

  const context = useMemo(() => ({ root }), [root]);

  return (
    <CanvasContext.Provider value={context}>
      {/* The comments panel docks beside the canvas rather than over it, so the
          wheel over the panel scrolls the panel. */}
      <Box sx={{ display: 'flex' }}>
        <Box
          ref={setRoot}
          onPointerDownCapture={onPointerDownCapture}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          sx={{
            position: 'relative',
            flex: 1,
            minWidth: 0,
            height: '100vh',
            overflow: 'hidden',
            overscrollBehavior: 'none',
            touchAction: 'none',
            cursor:
              hand === 'dragging' ? 'grabbing' : hand === 'ready' ? 'grab' : '',
          }}
        >
          <Box
            ref={layerRef}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              // As wide as the widest flow, and never narrower than the window:
              // the flows are what the page is the size of now that they no
              // longer scroll inside it.
              width: 'max-content',
              minWidth: '100%',
              transformOrigin: '0 0',
              // With the hand out, nothing under it answers, so the cursor is the
              // hand everywhere rather than a pointer over every button.
              pointerEvents: hand ? 'none' : undefined,
            }}
          >
            {children}
          </Box>

          {/* One floating bar for the page, the same pill a prototype floats:
              what the page brings (its version), then commenting, then zoom. */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            <Pill>
              {tools && (
                <>
                  {tools}
                  <Divider orientation='vertical' flexItem />
                </>
              )}
              <Segment
                icon='message'
                label='Comment (C)'
                selected={commenting}
                count={openThreads(comments).length}
                alert={hasUnread(comments, user?.id)}
                onClick={() => setCommenting(!commenting)}
              />
              {commenting && (
                <Segment
                  icon='list'
                  label='All comments'
                  selected={listOpen}
                  onClick={() => setListOpen(!listOpen)}
                />
              )}
              <Divider orientation='vertical' flexItem />
              <Segment icon='minus' label='Zoom out' onClick={() => step(-1)} />
              <Segment label='Zoom to 100%' onClick={() => zoomCentre(1)}>
                {Math.round(zoom * 100)}%
              </Segment>
              <Segment icon='plus' label='Zoom in' onClick={() => step(1)} />
              <Segment icon='expand' label='Zoom to fit' onClick={fit} />
            </Pill>
          </Box>
        </Box>
        <CanvasComments
          layer={layerEl}
          zoom={zoom}
          commenting={commenting}
          setCommenting={setCommenting}
          focus={focus}
        />
      </Box>
    </CanvasContext.Provider>
  );
};

export default Canvas;
